<template>
    <div class="charts gap-3 d-flex flex-column">
        <div class="chart" v-if="lineSeries()?.length != 0">
            <h5>{{$t(`feature.roles.$type.${role.patient}`)}}</h5>
            <apexchart height="350" id="lineCharts" :options="lineOptions(role.patient)" :series="lineSeries(role.patient)">
            </apexchart>
            <div class="small text-muted px-3" style="position: relative; top: -16px; font-size: 11px;"
                v-html="lines_x_label"></div>
            <!-- CAREGIVERS -->
            <template v-if="show_caregiver_series() ?? false">
                <h5 class="mt-3">{{$t(`feature.roles.$type.${role.caregiver}`)}}</h5>
                <apexchart height="350" id="lineCharts" :options="lineOptions(role.caregiver)" :series="lineSeries(role.caregiver)">
                </apexchart>
                <div class="small text-muted px-3" style="position: relative; top: -16px; font-size: 11px;"
                    v-html="lines_x_label"></div>
            </template>
        </div>
        <div class="chart row" v-if="barSeries">
            <template v-for="step_id in plot_groups.bar" :key="step_id">
                <div class="col-12 col-md-6 mb-3">
                    <h5 class="fs-14 text-body-secondary fw-bold m-0 text-center">
                        {{ getStep(step_id)?.title }}
                    </h5>
                    <apexchart height="350" :options="barOptions(step_id, getBarSeries(step_id))"
                        :series="getBarSeries(step_id)">
                    </apexchart>
                </div>
            </template>
        </div>
        <div class="chart" v-if="plot_groups.body_map">
            <template v-for="step_id in plot_groups.body_map" :key="step_id">
                <h5 class="fs-14 text-body-secondary fw-bold m-0 text-center">
                    {{ getStep(step_id)?.title }}
                </h5>
                <div class="row">
                    <div class="col-12 col-lg-6">
                        <h5 class="mt-3 text-center">{{$t(`feature.roles.$type.${role.patient}`)}}</h5>
                        <human-body-map-with-single-role :survey="survey" :values="body_map_groups[step_id]" :role="role.patient" :step_id="step_id" />
                    </div>
                    <div class="col-12 col-lg-6">
                        <h5 class="mt-3 text-center">{{$t(`feature.roles.$type.${role.caregiver}`)}}</h5>
                        <human-body-map-with-single-role :survey="survey" :values="body_map_groups[step_id]" :role="role.caregiver" :step_id="step_id" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ApexOptions } from 'apexcharts';
import { Survey, SurveyResponse, SurveyStepChartType, SurveyStepChoice } from '@/utils/models/survey.model';
import DateFormatUtils from '@/utils/methods/date_formats';
import { CLIENT, Environment } from '@/utils/const/env';
import API from '@/services/api/api.service';
import { PatientExtraDefinitionValue, Role } from '@/utils/models/user.model';
import { getChartConfig } from '@/utils/const/chart_config';
import HumanBodyMapWithSingleRole from './utils/HumanBodyMapWithSingleRole.vue';
import { SurveyUtils } from '@/utils/methods/survey_utils';


export default defineComponent({
    name: 'SurveyStatsChartWithRole',
    components: {
        HumanBodyMapWithSingleRole
    },
    props: {
        survey: {
            type: Object as PropType<Survey>,
            required: true
        },
        responses: {
            type: Object as PropType<SurveyResponse[]>,
            required: true
        },
    },
    data() {
        return {
            definition: undefined as number | undefined
        }
    },
    mounted() {
        if (this.env === 'kareena' && (this.survey.key?.includes('questionario-fisioterapia') ?? false)) {
            this.getExtraDefinitionValues()
        }
    },
    computed: {
        $s(): SurveyUtils {
            return new SurveyUtils(this.survey, this.responses ?? []);
        },
        env(): Environment { return CLIENT },
        show_threshold(): boolean {
            return this.env === 'kareena' && (this.survey.key?.includes('questionario-fisioterapia') ?? false) && this.definition !== undefined;
        },
        plot_groups(): SurveyStepChartInfo {
            let info = {} as SurveyStepChartInfo;
            this.survey.steps?.forEach((step, index) => {
                if (step.display_in_chart === true) {
                    info[step.chart_type!] = [...info[step.chart_type!] ?? [], step.id];
                }
            });
            return info;
        },
        
        bar_groups(): any {
            let obj = {};
            this.responses.forEach(res => {
                res.answers.forEach(ans => {
                    if (this.plot_groups.bar?.includes(ans.step_id)) {
                        const _default = {
                            // survey_id: res.survey_id
                        }
                        obj[`${ans.step_id}-${Role.patient}`] ??= { ..._default };
                        obj[`${ans.step_id}-${Role.caregiver}`] ??= { ..._default };
                        const from = res.created_by_role ?? Role.patient;

                        let value = this.$s.getAnswerValue(ans.step_id, this.responses.find((r) => r.id === res.id)!);
                        let step = this.getStep(ans.step_id);
                        (step?.choices as SurveyStepChoice[])?.forEach((choice) => {
                                obj[`${ans.step_id}-${Role.patient}`][choice.value] ??= 0;
                                obj[`${ans.step_id}-${Role.caregiver}`][choice.value] ??= 0;
                        });
                        if(Array.isArray(value)) {
                            value.forEach((v) => {
                                obj[`${ans.step_id}-${Role.patient}`][v] ??= 0;
                                obj[`${ans.step_id}-${Role.caregiver}`][v] ??= 0;
                                obj[`${ans.step_id}-${from}`][v]++;
                            })
                            
                        } else {
                            obj[`${ans.step_id}-${Role.patient}`][value] ??= 0;
                            obj[`${ans.step_id}-${Role.caregiver}`][value] ??= 0;
                            obj[`${ans.step_id}-${from}`][value]++;
                        }
                    }
                });
            });
            console.log('bar_groups', obj);
            return obj
        },
        barSeries(): any {
            return this.plot_groups.bar?.map(step_id => {
                return [{
                    id: `${step_id}-${Role.patient}`,
                    name: this.$t(`feature.roles.$type.${Role.patient}`),
                    data: Object.keys(this.bar_groups[`${step_id}-${Role.patient}`]).map((value) => this.bar_groups[`${step_id}-${Role.patient}`][value]),
                    step_id: step_id,
                    role: Role.patient
                },
                {
                    id: `${step_id}-${Role.caregiver}`,
                    name: this.$t(`feature.roles.$type.${Role.caregiver}`),
                    data: Object.keys(this.bar_groups[`${step_id}-${Role.caregiver}`]).map((value) => this.bar_groups[`${step_id}-${Role.caregiver}`][value]),
                    step_id: step_id,
                    role: Role.caregiver
                }
                ]
            }) ?? []
        },
        body_map_groups(): any {
            let obj = {};
            this.responses.forEach(res => {
                res.answers.forEach(ans => {
                    if (this.plot_groups.body_map?.includes(ans.step_id)) {
                        const from = res.created_by_role ?? Role.patient;
                        let step = this.getStep(ans.step_id);
                        let answer_ids = this.$s.getAnswerValue(ans.step_id, this.responses.find((r) => r.id === res.id)!) as number[];
                        let choices = Object.fromEntries((step?.choices as SurveyStepChoice[]).map((choice) => { return [choice.value, 0] }) ?? []);
                        Object.keys(choices).forEach((key) => {
                            if (answer_ids.map((v) => v.toString()).includes(key)) {
                                obj[ans.step_id] ??= {}
                                obj[ans.step_id][Role.patient] ??= {};
                                obj[ans.step_id][Role.caregiver] ??= {};
                                obj[ans.step_id][Role.patient][key] ??= 0;
                                obj[ans.step_id][Role.caregiver][key] ??= 0;
                                obj[ans.step_id][from][key]++;
                            }
                        });
                    }
                })
            });
            console.log('body_map_groups', obj);
            return obj
        },
        

        lines_x_label() {
            return getChartConfig(this.survey.key!)?.line_x_label;
        },
        role() {
            return Role;
        }

    },
    methods: {
        getStep(step_id) {
            return this.survey.steps.find(step => step.id === step_id);
        },
        getResponseByIndex(val: number, role?: Role): SurveyResponse | undefined {
            let responses;
            if (role !== undefined) {
                responses = this.responses.filter((res) => (res.created_by_role ?? Role.patient) == role);
            } else {
                responses = this.responses;
            }
            return [...responses].reverse()[val];
        },
        getExtraDefinitionValues() {
            API.getExtraDefinitionValues({ entity: 'patients', entity_id: this.$route.params.id as string }).then((response) => {
                if (response.data.success === true) {
                    if (this.survey.key?.includes('questionario-fisioterapia') ?? false) {
                        this.definition = ((response.data.data ?? []) as PatientExtraDefinitionValue[]).find((element) => element.dataextra_schema_name === 'physiotherapy_advice')?.dataextra_value[
                            this.survey.key === 'questionario-fisioterapia-casa' ? 'home_days' : 'therapist_days'
                        ] as number | undefined;
                    }
                }
            })
        },
        lineSeries(role?: Role) {
            return this.plot_groups.line?.map((step_id) => {
                return {
                    name: this.survey.steps.find((step) => step.id === step_id)?.title,
                    data: this.valid_responses(role).map((res) => parseInt(res.answers.find((ans) => ans.step_id === step_id)?.value ?? 'UNKNOW')).filter((el) => !Number.isNaN(el)),
                }
            }) ?? []
        },
        lineOptions(role?: Role): ApexOptions {
            const line_steps = this.survey.steps.filter((step) => this.plot_groups.line?.includes(step.id));
            const min = line_steps.some((step) => step.min_value !== undefined && step.min_value !== null) && line_steps.some((step) => step.max_value !== undefined && step.max_value !== 0) ? Math.min(...line_steps.map((e) => e.min_value!)) : undefined;
            const max = line_steps.some((step) => step.max_value !== undefined && step.max_value !== null) && line_steps.some((step) => step.max_value !== undefined && step.max_value !== 0) ? Math.max(...line_steps.map((e) => e.max_value!)) : undefined;
            return {
                type: 'line',
                chart: {
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: "smooth",
                    width: 4
                },
                colors: ["#006EAB", "#36adb2", '#06b48a', '#f3c44c', '#dc3545', '#80d6f2', '#ff822a', '#f061d6'],
                legend: {
                    showForSingleSeries: true,
                    position: "top",
                    fontSize: "12px",
                    fontWeight: 500,
                    horizontalAlign: "center",
                    labels: {
                        colors: "#8E8DA1",
                    },
                    itemMargin: {
                        vertical: 15,
                        horizontal: 12,
                    },
                    show: true,
                    showForNullSeries: true,
                },
                grid: {
                    show: true,
                    strokeDashArray: 5,
                    borderColor: "#d9e9ef",
                },
                tooltip: {
                    style: {
                        fontSize: "12px",
                    },
                    x: {
                        formatter: (val) => `${this.getResponseByIndex(val - 1, role)?.date ?? ''} - ${DateFormatUtils.HHmm(this.getResponseByIndex(val - 1, role)?.created_at!) ?? ''}`
                    },
                    y: {
                        formatter: function (val) {
                            return val?.toFixed(0) ?? '';
                        },
                    },
                },
                xaxis: {
                    categories: this.valid_responses(role).map((res) => DateFormatUtils.YYYYMMDD(res.date)),
                    labels: {
                        show: true,
                        style: {
                            colors: "#8e8da1",
                            fontSize: "10px",
                            fontWeight: 500,
                        },
                    },
                    axisTicks: {
                        show: true,
                    },
                    axisBorder: {
                        show: false,
                    },
                    tooltip: {
                        enabled: false,
                    },
                    /* title: {
                        text: getChartConfig(this.survey.key!)?.line_x_label,
                        style: {
                            fontWeight: 400,
                            color: "#8e8da1"
                        }
                    } */
                },
                yaxis: [{
                    show: true,
                    showForNullSeries: true,
                    showAlways: true,
                    min: min !== max ? min : undefined,
                    max: min !== max ? max : undefined,
                    labels: {
                        style: {
                            colors: ["#8e8da1"],
                            fontSize: "10px",
                        },
                        formatter(val) {
                            return val.toFixed(0)
                        },
                    },
                    axisTicks: {
                        show: true,
                    },
                    axisBorder: {
                        show: false,
                    },
                    title: {
                        text: getChartConfig(this.survey.key!)?.line_y_label,
                        style: {
                            fontWeight: 400,
                            color: "#8e8da1"
                        }
                    }
                },],
                annotations: this.show_threshold ? {
                    yaxis: [
                        {
                            y: this.definition ?? 10,
                            strokeDashArray: 0,
                            opacity: .3,
                            borderWidth: 2,
                            borderColor: '#06b48a',
                            label: {
                                borderColor: '#06b48a',
                                offsetY: 0,
                                offsetX: 20,
                                style: {
                                    color: '#fff',
                                    background: '#06b48a',
                                    fontSize: 10
                                },
                                text: this.$t('feature.surveys.treshold').toUpperCase(),
                            }
                        }
                    ]

                } : {
                    yaxis: [{}]
                }
            } as ApexOptions
        },
        show_caregiver_series(): boolean {
            try {
                return (this.lineSeries(Role.caregiver)?.filter((serie) => (serie?.data?.length ?? 0) != 0)?.length ?? 0) !== 0;
            } catch (error) {
                return false;
            }
        },
        valid_responses(role?: Role): SurveyResponse[] {
            let responses;
            if (role !== undefined) {
                responses = this.responses.filter((res) => (res.created_by_role ?? Role.patient) == role);
            } else {
                responses = this.responses;
            }
            return [...responses].reverse().filter((res) => res.answers != undefined && res.answers.length !== 0);
        },
        getBarSeries(step_id) {
            return this.barSeries?.flat().filter((el) => el.step_id == step_id);
        },
        barOptions(step_id, series): ApexOptions {
            const step = this.getStep(step_id);
            const min = 0;

            const datas = series.map((serie) => serie.data).flat();
            // const max: number | undefined = Math.max(...datas) ?? undefined;
            const max = undefined;

            return {
                type: 'bar',
                chart: {
                    type: 'bar',
                    // stacked: true,
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                dataLabels: {
                    enabled: true,
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: 30,
                        dataLabels: {
                            position: 'bottom',
                            total: {
                                enabled: true,
                                style: {
                                    fontSize: '16px',
                                }
                            },
                        },
                        distributed: false,
                    },
                },
                colors: ["#006EAB", "#36adb2", '#06b48a', '#f3c44c', '#dc3545', '#80d6f2', '#ff822a', '#f061d6'],
                stroke: {
                    show: true,
                    width: 1,
                    colors: ['white']
                },
                legend: {
                    position: "bottom",
                    fontSize: "12px",
                    fontWeight: 500,
                    horizontalAlign: "center",
                    labels: {
                        colors: "#8E8DA1",
                    },
                    itemMargin: {
                        vertical: 15,
                        horizontal: 12,
                    },
                    show: true,
                    showForNullSeries: false,
                },
                grid: {
                    show: true,
                    strokeDashArray: 5,
                    borderColor: "#d9e9ef",
                },
                tooltip: {
                    style: {
                        fontSize: "12px",
                    },
                    y: {
                        formatter: function (val) {
                            return val?.toFixed(0) ?? '';
                        },
                    },
                },
                xaxis: {
                    categories: (step?.choices as SurveyStepChoice[])?.map((choice) => choice.text) ?? [],
                    // categories: [... new Set(([...Object.keys(this.bar_groups[`${step_id}-${Role.patient}`]), ...Object.keys(this.bar_groups[`${step_id}-${Role.caregiver}`])]))],
                    labels: {
                        show: true,
                        style: {
                            colors: "#8e8da1",
                            fontSize: "12px",
                            fontWeight: 500,
                        },
                    },
                    axisTicks: {
                        show: false,
                    },
                    axisBorder: {
                        show: false
                    },
                },
                yaxis: {
                    labels: {
                        show: true,
                        style: {
                            colors: "#8e8da1",
                            fontSize: "10px",
                            fontWeight: 500,
                        },
                    },
                    min: min,
                    max: max,
                    forceNiceScale: true,
                    title: {
                        text: this.$t('feature.surveys.labels.bar_y_label'),
                        style: {
                            fontWeight: 400,
                            color: "#8e8da1"
                        }
                    }
                }
            } as ApexOptions
        }
    }
});

type SurveyStepChart = { [step_id: number]: SurveyStepChartType }
type SurveyStepChartInfo = {
    [type in SurveyStepChartType]: number[];
};
</script>